import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import UpsellList from "./UpsellList";
import UpsellForm from "./UpsellForm";
import FormModal from "../../../components/UI/FormModal/FormModal";
import { useSyncBotSelection } from "../../../hooks/useSyncBotSelection";

export default function Upsell() {
    const { botId } = useParams();
    const { showError } = useNotification();

    const [upsells, setUpsells] = useState([]);
    const [editing, setEditing] = useState(null);
    // Começa true: o fetch inicial só dispara depois do primeiro paint (useEffect),
    // então iniciar em false deixava a lista renderizar vazia por um instante
    // ("Nenhum upsell criado") antes do spinner/dados reais aparecerem.
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [currencySymbol, setCurrencySymbol] = useState('R$');
    const [revenueSymbol, setRevenueSymbol] = useState('R$');
    const [defaultDelivery, setDefaultDelivery] = useState(null);

    useSyncBotSelection();

    useEffect(() => {
        setShowForm(false);
        setEditing(null);
        if (botId) loadUpsells();
        else setLoading(false);
    }, [botId]);

    // Sem paginação: o bot tem no máx. 20 upsells, então carrega todos de uma vez.
    // silent=true → não mostra o spinner de tela cheia (evita piscar/subir ao topo
    // no refresh pós-ação); só troca os dados no lugar.
    const loadUpsells = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const { data } = await authService.upsell.get_upsell(botId, { page: 1, per_page: 20 });

            if (data.success) {
                setUpsells(data.upsells);
                setCurrencySymbol(data.currency_symbol || 'R$');
                setRevenueSymbol(data.revenue_symbol || 'R$');
                setDefaultDelivery(data.upsell_default_delivery || null);
            }
        } catch {
            showError("Erro ao carregar upsells");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleEdit = (upsell) => {
        setEditing({
            id: upsell.id,
            name: upsell.name,
            message: upsell.message,
            trigger_after: upsell.trigger_after,
            position_in_chain: upsell.position_in_chain,
            delay_minutes: upsell.delay_minutes,
            show_this_upsell_delivery: upsell.show_this_upsell_delivery,
            delivery: upsell.delivery || null,
            pix_values: upsell.pix_values || [],
            plan_names: upsell.plan_names || [],
            plan_durations: upsell.plan_durations || [],
            plan_color: upsell.plan_colors || upsell.plan_color || [],
            // Objetos completos por plano (inclui `delivery`) — sem isto,
            // deliveriesFromApi() não acha o array `plans` e zera a entrega
            // de todo plano ao abrir o form de edição.
            plans: upsell.plans || [],
            image_file_id: upsell.image_file_id,
            video_file_id: upsell.video_file_id,
            audio_file_id: upsell.audio_file_id,
            media_list: upsell.media_list || [],
            media_send_mode: upsell.media_send_mode || 'agrupado',
            action: 'edit'
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setEditing(null);
        setShowForm(false);
    };

    return (
        <>
            <UpsellList
                upsells={upsells}
                loading={loading}
                onEdit={handleEdit}
                onDuplicate={() => loadUpsells({ silent: true })}
                onDelete={() => loadUpsells({ silent: true })}
                onNew={() => {
                    setEditing(null);
                    setShowForm(true);
                }}
                botId={botId}
                currencySymbol={currencySymbol}
                revenueSymbol={revenueSymbol}
                defaultDelivery={defaultDelivery}
                onDefaultDeliveryChange={setDefaultDelivery}
            />

            {/* FORM — modal de criar/editar upsell */}
            <FormModal open={showForm} onClose={closeForm}>
                <UpsellForm
                    botId={botId}
                    editing={editing}
                    onEditingChange={setEditing}
                    onBack={closeForm}
                    onSuccess={() => {
                        closeForm();
                        loadUpsells();
                    }}
                />
            </FormModal>
        </>
    );
}
